
<template>
    <div
        v-show="visible"
        ref="dropdownRef"
        class="el-autocomplete-suggestion el-popper"
        role="region"
        :style="{ width: dropdownWidth }"
    >
        <el-scrollbar
            tag="ul"
            wrap-class="el-autocomplete-suggestion__wrap"
            view-class="el-autocomplete-suggestion__list"
        >
            <slot />
        </el-scrollbar>
    </div>
</template>

<script setup>
import { onMounted, ref, watch, computed, inject, nextTick, onBeforeUnmount } from 'vue';
import ElScrollbar from '@core/components/scrollbar/scrollbar.vue';
import { usePopper } from '@core/composables/usePopper.ts';

const props = defineProps({
    popperOptions: {
        type: Object,
        default: () => ({
            flipBehavior: ['bottom-start'],
            gpuAcceleration: false,
            boundariesElement: 'scrollParent',
        }),
    },
    id: { type: String, default: '' },
    maxHeight: { type: Number, default: 336 },
    popperClass: { type: String, default: 'el-autocomplete-suggestion'},
});

defineEmits(['update:visible']);

const dropdownRef = ref(null);
const dropdownWidth = ref('');

const autocomplete = inject('autocomplete', null);
const visible = computed(() => autocomplete?.visible ?? false);
const triggerRef = computed(() => autocomplete?.input ?? null);

const { initializePopper, updatePopper, destroyPopper } = usePopper(triggerRef, dropdownRef, {
    placement: 'bottom-start',
    modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'flip', options: { padding: 8, fallbackPlacements: ['top-start'] } },
        { name: 'preventOverflow', options: { boundary: 'viewport' } },
    ],
});

watch(visible, (val) => {
    if (!val) return destroyPopper();

    nextTick(async () => {
        const el = triggerRef?.value?.$el ?? triggerRef?.value;
        if (el) {
            dropdownWidth.value = `${el.offsetWidth}px`;
            await initializePopper();
            updatePopper();
        }
    });
});

onMounted(() => {
    nextTick(() => {
        const wrap = dropdownRef.value?.$el || dropdownRef.value;
        const list = wrap?.querySelector('.el-autocomplete-suggestion__list');
        if (list) {
            list.setAttribute('role', 'listbox');
            if (props.id) list.setAttribute('id', props.id);
        }
    });
});

onBeforeUnmount(() => {
    destroyPopper();
});
</script>
